import React, { useMemo, useState } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import { List, ListItemButton, ListItemText, ListItemIcon, Collapse, Box } from "@common/components/";
import { getMenusData } from "@redux/menus/selector";
import inventorySubRouter from "@clubInventory/router/subRouters";

/**
 * 店鋪庫存管理系統導覽選單
 * 支援圖示、子選單以及權限控管
 */
const InventoryNavMenu = () => {
	const location = useLocation();
	const menuData = useSelector(getMenusData);
	const [openMenus, setOpenMenus] = useState({});

	// 切換子選單展開/收合
	const handleToggle = key => e => {
		setOpenMenus(prev => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	// 來源直接使用後端 oriMenuData
	const sourceMenus = menuData?.oriMenuData || [];
	const { routeFrontKeySet, routeHrefMap } = useMemo(() => {
		const routeMap = inventorySubRouter.reduce((acc, route) => {
			if (!route?.frontKey) return acc;
			acc[route.frontKey] = route.href || route.path;
			return acc;
		}, {});

		return {
			routeFrontKeySet: new Set(Object.keys(routeMap)),
			routeHrefMap: routeMap,
		};
	}, []);
	// 外層決定呈現策略：只保留 route 交集 + isVisible=true
	const visibleIntersectedMenus = useMemo(() => {
		const filterMenus = menus =>
			menus
				.map(item => {
					const children = Array.isArray(item.children) ? filterMenus(item.children) : [];
					const isMatched = routeFrontKeySet.has(item.frontKey);
					const isVisible = item.isVisible !== false;
					if ((!isMatched || !isVisible) && children.length === 0) return null;

					return {
						...item,
						href: routeHrefMap[item.frontKey] || item.href,
						children: children.length > 0 ? children : undefined,
					};
				})
				.filter(Boolean);

		return filterMenus(sourceMenus);
	}, [sourceMenus, routeFrontKeySet, routeHrefMap]);

	return (
		<Box className="club-inventory-nav-menu">
			<List component="nav" disablePadding>
				{visibleIntersectedMenus.map(item => {
					const itemKey = item.key || item.menuID || item.frontKey;
					const itemLabel = item.label || item.menuName;
					const hasChildren = Array.isArray(item.children) && item.children.length > 0;
					const isOpen = !!openMenus[itemKey];

					// 判斷主選單是否應該呈現選取狀態（包含子選單選取時）
					const isActive = hasChildren ? item.children.some(child => child.href && location.pathname.startsWith(child.href)) : !!item.href && location.pathname.startsWith(item.href);

					return (
						<React.Fragment key={itemKey}>
							<ListItemButton
								component={hasChildren ? Box : NavLink}
								to={hasChildren ? undefined : item.href}
								onClick={hasChildren ? handleToggle(itemKey) : undefined}
								selected={!hasChildren && isActive}
								className={clsx("club-inventory-nav-item", !hasChildren && isActive && "club-inventory-nav-item-active")}
							>
								{item.icon && <ListItemIcon className="club-inventory-nav-icon">{item.icon}</ListItemIcon>}
								<ListItemText primary={itemLabel} />
							</ListItemButton>

							{hasChildren && (
								<Collapse in={isOpen} timeout="auto" unmountOnExit>
									<List component="div" disablePadding className="club-inventory-nav-submenu">
										{item.children.map(child => {
											const childKey = child.key || child.menuID || child.frontKey;
											const childLabel = child.label || child.menuName;
											const childActive = !!child.href && location.pathname.startsWith(child.href);
											return (
												<ListItemButton
													key={childKey}
													component={NavLink}
													to={child.href}
													selected={childActive}
													className={clsx("club-inventory-nav-subitem", childActive && "club-inventory-nav-item-active")}
												>
													{/* 子選單不顯示圖示，僅顯示文字 */}
													<ListItemText primary={childLabel} />
												</ListItemButton>
											);
										})}
									</List>
								</Collapse>
							)}
						</React.Fragment>
					);
				})}
			</List>
		</Box>
	);
};

export default InventoryNavMenu;
